/** Finance console endpoints (§16.2 Finance; docs/modules/payments.md). Shapes come from @windrop/contracts. */
import type {
  ApprovePayoutRequest,
  CompletePayoutRequest,
  CreatePayoutRequest,
  CreateRefundRequest,
  LedgerPageDto,
  LotteryFinanceDto,
  PaymentAdminRowDto,
  PayoutDetailDto,
  ReconciliationExceptionRowDto,
  ReconciliationOverviewDto,
  RefundRowDto,
  RefundWaveDto,
  ResolveReconciliationExceptionRequest,
  RetryPayoutRequest,
} from '@windrop/contracts';
import { api, type QueryValue } from './client';

export interface ListResponse<T> {
  items: T[];
}
export interface PageResponse<T> {
  items: T[];
  page: { nextCursor: string | null; limit: number };
}

export const financeApi = {
  reconciliation: (query?: Record<string, QueryValue>) =>
    api.get<ReconciliationOverviewDto>('/v1/admin/reconciliation', query),
  resolveException: (id: string, body: ResolveReconciliationExceptionRequest) =>
    api.post<ReconciliationExceptionRowDto>(`/v1/admin/reconciliation/exceptions/${id}/resolve`, body),
  runReconciliation: (body: { trigger: 'manual' | 'on_draw'; lotteryId?: string }) =>
    api.post<{ queued: boolean; jobId: string }>('/v1/admin/reconciliation/run', body, {
      idempotencyKey: false,
    }),
  payments: (query?: Record<string, QueryValue>) =>
    api.get<PageResponse<PaymentAdminRowDto>>('/v1/admin/payments', query),
  refunds: (query?: Record<string, QueryValue>) =>
    api.get<ListResponse<RefundRowDto>>('/v1/admin/refunds', query),
  createRefund: (body: CreateRefundRequest) => api.post<RefundRowDto>('/v1/admin/refunds', body),
  refundWave: (lotteryId: string, body: { dryRun: boolean; note?: string }) =>
    api.post<RefundWaveDto>(`/v1/admin/lotteries/${lotteryId}/refund-wave`, body),
  payouts: (query?: Record<string, QueryValue>) =>
    api.get<ListResponse<PayoutDetailDto>>('/v1/admin/payouts', query),
  createPayout: (body: CreatePayoutRequest) => api.post<PayoutDetailDto>('/v1/admin/payouts', body),
  approvePayout: (id: string, body: ApprovePayoutRequest) =>
    api.post<PayoutDetailDto>(`/v1/admin/payouts/${id}/approve`, body),
  rejectPayout: (id: string, reason: string) =>
    api.post<PayoutDetailDto>(`/v1/admin/payouts/${id}/reject`, { reason }),
  completePayout: (id: string, body: CompletePayoutRequest) =>
    api.post<PayoutDetailDto>(`/v1/admin/payouts/${id}/complete`, body),
  retryPayout: (id: string, body: RetryPayoutRequest) =>
    api.post<PayoutDetailDto>(`/v1/admin/payouts/${id}/retry`, body),
  ledger: (query?: Record<string, QueryValue>) => api.get<LedgerPageDto>('/v1/admin/ledger', query),
  lotteryFinance: (lotteryId: string) =>
    api.get<LotteryFinanceDto>(`/v1/admin/lotteries/${lotteryId}/finance`),
};
